<script lang="ts">
    import { v4 as uuidv4 } from "uuid"
    import Icon from "../../widgets/Icon.svelte"
    import { mdiEmail, mdiFileDocumentMultipleOutline, mdiShareVariant } from "@mdi/js"
    import { getToken, updateToken, deleteToken, createToken, postMail } from "../../../api"
    import { newNotification } from "../../../store"
    import { _ } from "svelte-i18n"
    import Modal from "../../widgets/Modal.svelte"
    import { tooltip } from "../../../helper"
    import { initialLocale } from "../../../i18n"

    export let operation: Operation | OperationOut

    let show: boolean = false
    let link: string
    let email: string
    let linkInput: HTMLInputElement
    let token: AccessToken

    const getTokenForOperation = async () => {
        let response = await getToken(operation.id, operation.collectionName || "operation")
        if (response.data.length) {
            setToken(response.data[0])
            generateLink()
        }
    }
    $: if (show) getTokenForOperation()

    const generateLink = () => {
        link = null

        if (token) {
            let collection = operation.collectionName === "operation-out" ? "opo" : "op"
            link = `${document.location.origin}/${initialLocale}/${collection}/${operation.id}/${token.token}`
        } else {
            link = ""
        }
    }

    const reGenerateLink = async () => {
        token.token = uuidv4()
        const permission = {
            method: "GET",
            collection: operation.collectionName,
            id: token.id,
            projection: "token",
        }
        const response = await updateToken(token, permission)
        if (response) {
            setToken(response.data)
            generateLink()
            newNotification({
                class: "success",
                html: $_("operations.linkRegenerated"),
                timeout: 8000,
            })
        }
    }

    const deactivateLink = async () => {
        const response = await deleteToken(token)
        if (response) {
            link = null
            setToken(null)
            newNotification({
                class: "success",
                html: $_("operations.linkDeactivated"),
                timeout: 8000,
            })
        }
    }

    const createTokenForOperation = async () => {
        const response = await createToken({
            token: uuidv4(),
            permissions: [
                {
                    method: "GET",
                    collection: operation.collectionName,
                    id: operation.id,
                    projection: "token",
                },
            ],
        })
        if (response) {
            setToken(response.data)
            generateLink()
        }
    }

    const setToken = (t: AccessToken) => {
        token = t
        generateLink()
    }

    const sendEmail = () => {
        if (validateEmail(email)) {
            postMail("sendPublicOperationLink", {
                link: link,
                to: email,
            })
                .then(() => {
                    newNotification({
                        class: "success",
                        html: $_("operations.linkSent", { values: { email } }),
                        timeout: 8000,
                    })
                    email = null
                })
                .catch((e) => {
                    console.error(e)
                    // shows error via postMail
                })
        } else {
            newNotification({
                class: "error",
                html: $_("operations.invalidEmail", { values: { email } }),
                timeout: 3000,
            })
        }
    }

    const validateEmail = (email: string) => {
        return email.match(
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    }

    const copyTextToClipboard = async () => {
        const textToCopy = linkInput.value
        try {
            await navigator.clipboard.writeText(textToCopy)

            newNotification({
                class: "success",
                html: $_("operations.linkCopied"),
                timeout: 3000,
            })
        } catch (err) {
            console.error("Link konnte nicht automatisch kopiert werden!", err)

            newNotification({
                class: "error",
                html: $_("operations.linkCopyFailed", { values: { text: textToCopy } }),
                timeout: 3000,
            })
        }
    }
</script>

{#if operation}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
        class="share-offer-icon"
        role="button"
        tabindex="0"
        on:click="{() => (show = true)}"
        use:tooltip="{{ content: $_('operations.shareTransaction') }}"
    >
        <Icon path="{mdiShareVariant}" size="1.2rem" />
    </div>
{/if}
<Modal cssClass="share-offer-modal" bind:show size="lg">
    <div slot="header">
        {operation?.type === 2 ? $_("operations.order") : $_("operations.offer")}
        {$_("general.share")}
    </div>

    {#if !token}
        <div>
            {$_("operations.linkDescription")}<br />
            {$_("operations.noPriceInfo")}
        </div>
    {:else}
        <div class="input-hint">{$_("operations.copyLinkHint")}</div>
        <div class="input-row">
            <div class="inputholder has-append no-input-margin">
                <input type="text" readonly data-cy="link" bind:value="{link}" bind:this="{linkInput}" />
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                    class="input-append transparent pr-0"
                    on:click="{() => copyTextToClipboard()}"
                    title="{$_('operations.copyLink')}"
                    role="button"
                    tabindex="0"
                >
                    <button class="btn" on:click|stopPropagation="{() => copyTextToClipboard()}">
                        <span>{$_("general.copy")}</span>
                        <Icon path="{mdiFileDocumentMultipleOutline}" size="1.2rem" />
                    </button>
                </div>
            </div>
        </div>
        <div class="layout layout-gap-md">
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
                class="regenerate-link mb-md"
                on:click="{reGenerateLink}"
                role="button"
                tabindex="0"
                use:tooltip="{{
                    content: $_('operations.regenerateLinkTooltip'),
                }}"
            >
                {$_("operations.regenerateLink")}
            </div>
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
                class="deactivete-link mb-md"
                on:click="{deactivateLink}"
                data-cy="deactivate-link"
                role="button"
                tabindex="0"
                use:tooltip="{{
                    content: $_('operations.deactivateLinkTooltip'),
                }}"
            >
                {$_("operations.deactivateLink")}
            </div>
        </div>
        <label for="email-link">{$_("operations.sendLinkByEmail")}</label>
        <div class="input-hint">{$_("operations.enterEmailHint")}</div>
        <div class="input-row">
            <div class="inputholder has-append no-input-margin">
                <input
                    id="email-link"
                    type="email"
                    placeholder="{$_('operations.emailPlaceholder')}"
                    bind:value="{email}"
                />
                <div class="input-append transparent pr-0">
                    <button
                        class="btn"
                        data-cy="send-share-offer"
                        on:click|stopPropagation="{sendEmail}"
                        disabled="{!email}"
                    >
                        <span>{$_("general.send")}</span>
                        <Icon path="{mdiEmail}" size="1.2rem" />
                    </button>
                </div>
            </div>
        </div>
    {/if}

    <div slot="footer">
        <button class="btn btn-secondary" on:click="{() => (show = false)}">{$_("general.close")}</button>

        {#if !link}
            <button data-cy="share-btn" class="btn" on:click="{createTokenForOperation}">
                {$_("operations.share")}
            </button>
        {/if}
    </div>
</Modal>
